import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { LANGUAGE_OPTIONS, setLanguagePreference, type LanguagePreference } from "./i18n";
import { ApiClient, type SessionState } from "./lib/api";

interface AuthContextValue {
  session: SessionState | null;
  api: ApiClient;
  isAuthOpen: boolean;
  openAuth: () => void;
  closeAuth: () => void;
  setSession: (session: SessionState) => void;
  logout: () => Promise<void>;
}

const STORAGE_KEY = "raven-scribe.session";
const AuthContext = createContext<AuthContextValue | null>(null);

function loadSession(): SessionState | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null") as SessionState | null;
    if (!parsed?.token || !parsed.workspace_id || parsed.expires_at_ms <= Date.now()) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<SessionState | null>(loadSession);
  const [isAuthOpen, setAuthOpen] = useState(false);
  const setSession = useCallback((next: SessionState) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setSessionState(next);
    setAuthOpen(false);
  }, []);
  const api = useMemo(() => new ApiClient(() => session), [session]);
  useEffect(() => {
    if (session) return;
    let active = true;
    void api.createGuestSession().then((guest) => {
      if (active) setSession(guest);
    }).catch(() => undefined);
    return () => { active = false; };
  }, [api, session, setSession]);
  useEffect(() => {
    if (!session?.user || session.administrator_role !== undefined) return;
    void api.getMe().then((me) => setSession({
      ...session,
      administrator_role: me.administrator_role ?? null,
      user: {
        ...session.user!,
        display_name: me.user.display_name,
        avatar_url: me.user.avatar_url,
        ui_language: me.user.ui_language,
        resolved_ui_language: me.user.resolved_ui_language,
      },
    }))
      .catch(() => undefined);
  }, [api, session, setSession]);
  useEffect(() => {
    const preference = session?.user?.ui_language;
    if (!LANGUAGE_OPTIONS.some(([candidate]) => candidate === preference)) return;
    void setLanguagePreference(preference as LanguagePreference);
  }, [session?.user?.ui_language]);
  useEffect(() => {
    const parameters = new URLSearchParams(window.location.hash.replace(/^#/u, ""));
    const handoff = parameters.get("oauth_handoff");
    if (!handoff) return;
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    void api.exchangeOAuthHandoff(handoff).then(setSession).catch(() => setAuthOpen(true));
  }, [api, setSession]);
  const logout = useCallback(async () => {
    try {
      if (session) await api.logout();
    } finally {
      localStorage.removeItem(STORAGE_KEY);
      setSessionState(null);
    }
  }, [api, session]);
  const value = useMemo<AuthContextValue>(() => ({
    session,
    api,
    isAuthOpen,
    openAuth: () => setAuthOpen(true),
    closeAuth: () => setAuthOpen(false),
    setSession,
    logout,
  }), [api, isAuthOpen, logout, session, setSession]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
